<template>
  <div class="customer-care-page">
    <!-- PAGE HERO -->
    <CustomerCareHero
      :title="heading"
      subtitle="We're here to help. Find answers, track your order, or get in touch with our team."
    />

    <!-- MAIN LAYOUT -->
    <div class="main-layout">
      <!-- Sidebar -->
      <CustomerCareSidebar
        ref="sidebar"
        :sections="sections"
        :active-id="activeId"
        @navigate="scrollToSection"
      />

      <!-- Content -->
      <div class="care-content">
        <p class="care-intro reveal">
          At Hooligans Eyewear, we want every purchase to feel effortless — from
          browsing to delivery. Below you'll find everything you need to know
          about shipping, returns, and how we ensure every pair is 100%
          authentic.
        </p>

        <!-- SHIPPING -->
        <CareSection
          class="reveal"
          section-id="shipping"
          eyebrow="Shipping"
          title="Shipping &amp; Delivery"
        >
          <p>
            All orders are shipped directly from our own in-house inventory — no
            drop shipping, no third-party warehouses. That means faster
            processing and full accountability at every step.
          </p>

          <ShippingTimeline :steps="shippingSteps" />

          <p>
            U.S. standard shipping is free. International shipping is $20. U.S.
            express shipping is available for $15. Available options and
            estimated delivery dates are shown at checkout.
          </p>

          <CareCallout variant="yellow">
            <strong>International delivery:</strong> Shipping costs $20 per
            order and takes 7–20 business days, depending on destination.
            Customs duties and import taxes are the buyer's responsibility.
          </CareCallout>
        </CareSection>

        <!-- RETURNS -->
        <CareSection
          class="reveal"
          section-id="returns"
          eyebrow="Returns"
          title="Returns &amp; Exchanges"
        >
          <p>
            If you're not fully satisfied with your purchase, we accept returns
            within <strong>14 days</strong> of delivery — no questions asked —
            provided the item is unworn and in original condition with all
            packaging intact.
          </p>

          <ReturnSteps :steps="returnSteps" />

          <CareCallout>
            <strong>Final Sale items and international orders</strong> are not
            eligible for return or exchange. Other eligible U.S. orders qualify
            under our standard 14-day policy; a $9.95 return shipping fee
            applies.
          </CareCallout>
        </CareSection>

        <!-- ORDERS -->
        <CareSection
          class="reveal"
          section-id="orders"
          eyebrow="Orders"
          title="Orders &amp; Tracking"
        >
          <p>
            Once your order is placed, you'll receive a confirmation email
            immediately. A second email with your tracking number is sent as
            soon as the order ships.
          </p>

          <ul class="care-list">
            <li>
              Track your shipment using the link in your confirmation email, or
              directly on the carrier's website.
            </li>
            <li>
              To modify or cancel an order, contact us within 1 hour of placing
              it — after that, orders enter fulfillment and cannot be changed.
            </li>
            <li>
              If your package is delayed or marked delivered but not received,
              contact us and we'll investigate with the carrier immediately.
            </li>
            <li>
              Damaged items must be reported within 48 hours of delivery with
              photos. We will arrange a replacement or full refund.
            </li>
          </ul>

          <CareCallout variant="blue">
            <strong>Order number:</strong> always have it ready when you contact
            us — it speeds up every enquiry significantly.
          </CareCallout>
        </CareSection>

        <!-- AUTHENTICITY -->
        <CareSection
          class="reveal"
          section-id="authenticity"
          eyebrow="Our Promise"
          title="Authenticity Guarantee"
        >
          <p>
            Every single pair sold on hooliganseyewear.com is 100% authentic,
            sourced directly from authorised brand distributors. We hold our
            entire inventory in-house, meaning we personally verify and handle
            each product before it ships to you.
          </p>

          <GuaranteeStrip :items="guaranteeItems" />

          <p>
            We take pride in offering only genuine eyewear. If you ever have
            reason to doubt the authenticity of your purchase, contact us — we
            will resolve it fully, no questions asked.
          </p>
        </CareSection>

        <!-- FAQ -->
        <CareSection
          class="reveal"
          section-id="faq"
          eyebrow="FAQ"
          title="Frequently Asked Questions"
        >
          <p>Quick answers to our most common questions.</p>
          <FaqAccordion :items="faqItems" />
        </CareSection>

        <!-- CTA -->
        <CustomerCareCta class="reveal" />
      </div>
    </div>
  </div>
</template>

<script>
import "./customer_care.css";

import CustomerCareHero from "~/components/customer-care/CustomerCareHero.vue";
import CustomerCareSidebar from "~/components/customer-care/CustomerCareSidebar.vue";
import CareSection from "~/components/customer-care/CareSection.vue";
import CareCallout from "~/components/customer-care/CareCallout.vue";
import ShippingTimeline from "~/components/customer-care/ShippingTimeline.vue";
import ReturnSteps from "~/components/customer-care/ReturnSteps.vue";
import GuaranteeStrip from "~/components/customer-care/GuaranteeStrip.vue";
import FaqAccordion from "~/components/customer-care/FaqAccordion.vue";
import CustomerCareCta from "~/components/customer-care/CustomerCareCta.vue";
import { useStaticPageSeo } from "~/core/composables/useStaticPageSeo";

export default {
  name: "CustomerCarePage",
  setup() {
    return useStaticPageSeo({
      title: "Customer Care | Hooligans Eyewear",
      heading: "Customer Care",
      description:
        "Contact Hooligans Eyewear support and find answers about shipping, returns, order tracking, authenticity, and customer service hours.",
    });
  },
  components: {
    CustomerCareHero,
    CustomerCareSidebar,
    CareSection,
    CareCallout,
    ShippingTimeline,
    ReturnSteps,
    GuaranteeStrip,
    FaqAccordion,
    CustomerCareCta,
  },
  data() {
    return {
      activeId: "shipping",
      sections: [
        { id: "shipping", long: "Shipping & Delivery" },
        { id: "returns", long: "Returns & Exchanges" },
        { id: "orders", long: "Orders & Tracking" },
        { id: "authenticity", long: "Authenticity Guarantee" },
        { id: "faq", long: "FAQ" },
      ],
      shippingSteps: [
        {
          num: "01",
          label: "Same day",
          title: "Order processed",
          desc: "Orders placed before 2pm EST are processed the same business day.",
        },
        {
          num: "02",
          label: "Same/next day",
          title: "Packed & shipped",
          desc: "Each order is carefully packed and handed to our carrier.",
        },
        {
          num: "03",
          label: "1–6 days",
          title: "Standard delivery",
          desc: "Estimated U.S. domestic delivery within 1–6 business days.",
        },
        {
          num: "04",
          label: "Tracking",
          title: "You're notified",
          desc: "A tracking number is emailed to you as soon as your order ships.",
        },
      ],
      returnSteps: [
        {
          num: 1,
          title: "Contact us",
          desc: 'Email <a href="mailto:support@hooliganseyewear.com">support@hooliganseyewear.com</a> with your order number and reason for return. We\'ll reply within 24 hours.',
        },
        {
          num: 2,
          title: "Ship it back",
          desc: "We'll provide a prepaid return label. Pack the item securely in its original case and box.",
        },
        {
          num: 3,
          title: "We inspect & process",
          desc: "Once we receive and inspect the return, refunds are processed within 1–5 business days to your original payment method.",
        },
        {
          num: 4,
          title: "Exchange (optional)",
          desc: "Prefer a different frame or size? Let us know and we'll arrange a direct exchange at no additional shipping cost.",
        },
      ],
      guaranteeItems: [
        { label: "Sourced from", value: "Authorised distributors" },
        { label: "Storage", value: "100% in-house inventory" },
        { label: "Our guarantee", value: "Authentic or full refund" },
      ],
      faqItems: [
        {
          question: "Do you ship internationally?",
          answer:
            "Yes. International shipping costs $20 per order and takes 7–20 business days, depending on destination. Available countries are shown at checkout. Customs duties and import taxes are the buyer's responsibility.",
        },
        {
          question: "Can I return prescription eyewear?",
          answer:
            "Non-prescription frames are returnable within 14 days of delivery. If lenses have been custom-fitted or prescription-ground, returns are accepted only if the item is defective or the prescription was fulfilled incorrectly. Contact us and we'll walk you through the process.",
        },
        {
          question: "What payment methods do you accept?",
          answer:
            "We accept Visa, Mastercard, American Express, Discover, Google Pay, Amazon Pay, Afterpay, Klarna, Affirm, and Cash App Pay. All transactions are secured with SSL encryption.",
        },
        {
          question: "How do I know my size before buying?",
          answer:
            "Every product listing includes the manufacturer's full measurements: lens width, bridge width, and temple length in millimetres (e.g. 52-18-145). Check the inside arm of any pair you own for your current size, then match accordingly. If you're unsure, email us — we're happy to advise.",
        },
        {
          question: "What if my order arrives damaged?",
          answer:
            'Please photograph the damage and contact us at <a href="mailto:support@hooliganseyewear.com">support@hooliganseyewear.com</a> within 48 hours of delivery. We will arrange a full replacement or refund — whichever you prefer — at no cost to you.',
        },
        {
          question: "Can I use more than one discount code?",
          answer:
            "Only one discount code can be applied per order. Codes cannot be combined with other promotions or applied to Final Sale items. If you have questions about a specific promotion, contact us before placing your order.",
        },
      ],
    };
  },
  mounted() {
    if (typeof window === "undefined") return;

    this._reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this._onScroll = () => this._updateActive();
    window.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("resize", this._onScroll, { passive: true });
    this.$nextTick(() => this._updateActive());
    this._setupRevealObserver();
  },
  beforeUnmount() {
    if (typeof window === "undefined") return;
    if (this._onScroll) {
      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onScroll);
    }
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  methods: {
    _setupRevealObserver() {
      const els = this.$el.querySelectorAll(".reveal");
      if (!els.length) return;

      if (this._reduceMotion || typeof IntersectionObserver === "undefined") {
        els.forEach((el) => el.classList.add("visible"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              this._revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      els.forEach((el) => this._revealObserver.observe(el));
    },
    _getClickOffset() {
      if (typeof window === "undefined") return 24;
      return window.innerWidth <= 900 ? 80 : 24;
    },
    _updateActive() {
      const els = this.sections
        .map((s) => ({ id: s.id, el: document.getElementById(s.id) }))
        .filter((x) => x.el);
      if (!els.length) return;

      let current = els[0].id;
      for (const { id, el } of els) {
        if (el.getBoundingClientRect().top <= 120) current = id;
      }
      if (current !== this.activeId) {
        this.activeId = current;
      }
    },
    scrollToSection(id) {
      if (typeof window === "undefined") return;
      const target = document.getElementById(id);
      if (!target) return;
      const offset = this._getClickOffset();
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
      this.activeId = id;
    },
  },
};
</script>
